class CartCrosssellNew extends HTMLElement {
  constructor() {
    super();
    
    // Add a small delay to ensure the DOM is fully ready
    // This helps prevent race conditions with other scripts
    setTimeout(() => {
      this.init();
    }, 50);
  }
  
  // Separated initialization to allow for potential reinitialization
  init() {
    // Find required elements
    this.slider = this.querySelector('.cart-crosssell-new__slider');
    this.sliderContainer = this.querySelector('.slider-container');
    this.quickAddButtons = this.querySelectorAll('[data-quick-add-button]');
    
    // Skip if essential elements are missing
    if (!this.slider || !this.sliderContainer) {
      console.warn('Could not initialize cart cross-sell - missing required elements');
      return;
    }
    
    // Flag to track if a refresh is in progress
    this.isRefreshing = false;
    
    // Create or find the modal
    this.setupModal();
    
    // Initialize the simplified slider
    this.initSimplifiedSlider();
    
    this.bindQuickAddButtons();
    
    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = undefined;
    this.setupCartUpdateListener();
    
    // Initialize dynamic prices for sculptor products
    this.updateSculptorCrosssellPrices();
    
    // Add mutation observer to detect if our slider is damaged by external scripts
    this.setupResilienceMonitor();
    
    console.log('Cart cross-sell initialized successfully');
  }
  
  // Subscribe to cart update events (same pattern as cart.js)
  setupCartUpdateListener() {
    // Listen for custom cart:updated events (from legacy code)
    this.cartUpdateUnsubscriber = subscribe('cart:updated', (event) => {
      this.onCartUpdate(event.detail.cart);
    });

    // Also listen for standard Shopify cart update events
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      this.shopifyCartUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Only process if the event contains cart data and didn't come from us
        if (event.cartData && event.source !== 'cart-crosssell-internal') {
          console.log('Cart updated via Shopify events:', event.source);
          this.onCartUpdate(event.cartData);
        }
      });
    }

    // Clean up previous subscriptions to prevent duplicates
    document.removeEventListener('cart:refresh', this.onCartRefresh); // Remove any existing listeners first
    this.onCartRefresh = this.onCartRefresh.bind(this); // Bind once and save reference
    document.addEventListener('cart:refresh', this.onCartRefresh);
  }
  
  // Initialize our isolated slider with just 2 positions
  initSimplifiedSlider() {
    // Create an isolated slider that's completely independent
    // from other JavaScript on the page
    const createIsolatedSlider = (container, indicator, statusElement) => {
      if (!container || !indicator) return null;
      
      // Private state variables in closure
      let isDragging = false;
      let dragStartX = 0;
      let startScrollLeft = 0;
      let currentPosition = 0;
      let isTransitioning = false;
      
      // Force initial position to zero and store required DOM elements
      container.scrollLeft = 0;
      
      // Calculate dimensions - recalculate when needed for responsiveness
      const getItemWidth = () => container.clientWidth * 0.5;
      const getTotalScrollWidth = () => getItemWidth(); // With 3 items at 50% width, max scroll is 50%
      
      // Position tracking - isolated from other code
      const getCurrentPosition = () => {
        const scrollPosition = container.scrollLeft;
        const threshold = getTotalScrollWidth() * 0.5;
        return scrollPosition > threshold ? 1 : 0;
      };
      
      // UI Updates - self-contained
      const updateIndicator = () => {
        const position = getCurrentPosition();
        currentPosition = position;
        indicator.setAttribute('data-position', position.toString());
        
        // Update screen reader if available
        if (statusElement) {
          statusElement.textContent = position === 0 
            ? 'Showing items 1 and 2 of 3' 
            : 'Showing items 2 and 3 of 3';
        }
      };
      
      // Snapping logic - completely self-contained
      const snapToPosition = (position = null) => {
        if (isTransitioning) return;
        
        // If no position provided, calculate based on current scroll
        const targetPosition = position !== null ? position : getCurrentPosition();
        const scrollTarget = targetPosition === 1 ? getTotalScrollWidth() : 0;
        
        // Prevent interference during transition
        isTransitioning = true;
        
        // Apply smooth scrolling
        container.scrollTo({
          left: scrollTarget,
          behavior: 'smooth'
        });
        
        // Update indicator immediately for responsive feel
        currentPosition = targetPosition;
        indicator.setAttribute('data-position', targetPosition.toString());
        
        // Update screen reader info
        if (statusElement) {
          statusElement.textContent = targetPosition === 0 
            ? 'Showing items 1 and 2 of 3' 
            : 'Showing items 2 and 3 of 3';
        }
        
        // Clear transitioning flag after animation completes
        setTimeout(() => {
          isTransitioning = false;
        }, 300);
      };
      
      // Pointer event handlers (using named functions for proper cleanup)
      const handlePointerDown = (e) => {
        // Stop event propagation to prevent other handlers from interfering
        e.stopPropagation();
        
        // Only handle primary button (usually left mouse button)
        if (e.button !== 0 && e.button !== undefined) return;
        
        isDragging = true;
        dragStartX = e.clientX;
        startScrollLeft = container.scrollLeft;
        container.classList.add('is-dragging');
        
        // Explicitly capture pointer to ensure we receive all events
        try {
          container.setPointerCapture(e.pointerId);
        } catch (err) {
          console.warn('Pointer capture not supported', err);
        }
      };
      
      const handlePointerMove = (e) => {
        if (!isDragging) return;
        
        // Stop event propagation
        e.stopPropagation();
        
        // Calculate distance moved
        const dx = e.clientX - dragStartX;
        
        // Update scroll position - opposite of drag direction for natural feel
        container.scrollLeft = startScrollLeft - dx;
      };
      
      const handlePointerUp = (e) => {
        if (!isDragging) return;
        
        // Stop event propagation
        e.stopPropagation();
        
        // Release pointer capture
        try {
          container.releasePointerCapture(e.pointerId);
        } catch (err) {
          // Ignore errors if pointerId is no longer valid
        }
        
        isDragging = false;
        container.classList.remove('is-dragging');
        
        // Calculate drag direction and distance
        const dragDistance = e.clientX - dragStartX;
        
        // If significant drag, determine direction
        if (Math.abs(dragDistance) > 20) {
          // Drag right → show first position
          // Drag left → show second position
          snapToPosition(dragDistance > 0 ? 0 : 1);
        } else {
          // For small movements, snap to nearest
          snapToPosition();
        }
      };
      
      const handlePointerCancel = (e) => {
        // Always clean up, even if we weren't dragging
        // This ensures we never leave the slider in a dragging state
        
        // Stop event propagation
        if (e) e.stopPropagation();
        
        // Try to release pointer capture if possible
        try {
          if (e && e.pointerId !== undefined) {
            container.releasePointerCapture(e.pointerId);
          }
        } catch (err) {
          // Ignore errors if pointerId is no longer valid
        }
        
        // Reset state and remove dragging class
        isDragging = false;
        container.classList.remove('is-dragging');
        
        // Snap to nearest position
        snapToPosition();
      };
      
      // Extra handler for mobile touch cancel events
      // This can happen when the system takes over (pull-to-refresh, etc.)
      const handleTouchCancel = () => {
        console.log('Touch cancel event fired');
        // Use the pointer cancel handler to clean up
        handlePointerCancel();
      };
      
      // Keyboard navigation - fully self-contained
      const handleKeyDown = (e) => {
        // Stop event propagation
        e.stopPropagation();
        
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          snapToPosition(1);
          e.preventDefault();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          snapToPosition(0);
          e.preventDefault();
        }
      };
      
      // Scroll event handling - debounced internally
      let scrollTimeout;
      const handleScroll = (e) => {
        // Stop event propagation
        e.stopPropagation();
        
        // Don't interfere with active dragging
        if (!isDragging && !isTransitioning) {
          updateIndicator();
        }
        
        // Clear previous timeout
        clearTimeout(scrollTimeout);
        
        // Set new timeout for snapping after scroll stops
        scrollTimeout = setTimeout(() => {
          if (!isDragging && !isTransitioning) {
            snapToPosition();
          }
        }, 150);
      };
      
      // Clean all event listeners on cleanup
      const destroy = () => {
        container.removeEventListener('pointerdown', handlePointerDown);
        container.removeEventListener('pointermove', handlePointerMove);
        container.removeEventListener('pointerup', handlePointerUp);
        container.removeEventListener('pointercancel', handlePointerCancel);
        container.removeEventListener('touchcancel', handleTouchCancel); // Remove touchcancel listener
        container.removeEventListener('keydown', handleKeyDown);
        container.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);
      };
      
      // Initialize by setting up all event listeners
      // Use non-passive listeners where needed to prevent default behavior
      container.addEventListener('pointerdown', handlePointerDown);
      container.addEventListener('pointermove', handlePointerMove, { passive: false });
      container.addEventListener('pointerup', handlePointerUp);
      container.addEventListener('pointercancel', handlePointerCancel);
      container.addEventListener('touchcancel', handleTouchCancel); // Add touchcancel listener for mobile
      container.addEventListener('keydown', handleKeyDown);
      container.addEventListener('scroll', handleScroll, { passive: true });
      
      // Set initial state
      updateIndicator();
      
      // Return public API
      return {
        destroy,
        snapToPosition,
        updateIndicator,
        getCurrentPosition: () => currentPosition
      };
    };
    
    // Create the isolated slider instance
    this.isolatedSlider = createIsolatedSlider(
      this.sliderContainer,
      this.querySelector('.slider-indicator__progress'),
      this.querySelector('#slider-status')
    );
    
    // Store reference for cleanup
    this._destroySlider = () => {
      if (this.isolatedSlider) {
        this.isolatedSlider.destroy();
        this.isolatedSlider = null;
      }
    };
  }
  
  onCartRefresh(event) {
    // Prevent triggering our own events 
    if (event && event.detail && event.detail.source === 'cart-crosssell-internal') {
      return; // Skip if we triggered this ourselves
    }
    
    // Update bundle price if modal is open
    this.updateBundlePriceIfVisible();
    
    // Also update crosssell prices
    this.updateSculptorCrosssellPrices();
    
    this.refreshCart(true); // Pass flag to indicate internal call
  }
  
  onCartUpdate(cart) {
    // Handle undefined cart or empty cart object
    if (!cart) {
      console.log('Received cart update with no cart data');
      // Try to fetch cart data if not provided
      fetch('/cart.js')
        .then(response => response.json())
        .then(cartData => {
          if (cartData && cartData.item_count === 0) {
            this.handleEmptyCart();
          }
        })
        .catch(error => console.error('Error fetching cart data:', error));
      return;
    }
    
    // Handle empty cart state
    if (cart.item_count === 0 || cart.items?.length === 0) {
      this.handleEmptyCart();
      return;
    }
    
    // Normal update for non-empty cart
    // Make sure cross-sell is visible for non-empty cart
    const upsellContainer = this.closest('.cart-drawer__upsell');
    if (upsellContainer) {
      upsellContainer.style.removeProperty('display');
    }
    
    // Check if the cart contains any sculptor products
    const sculptorHandles = ['sculptor-2-0', 'sculptor-2-0-copy'];
    const hasScultorProduct = cart.items?.some(item => 
      item.handle && sculptorHandles.includes(item.handle.toLowerCase())
    );
    
    // If we detect no sculptor products, we may be about to experience a cart clear
    // Let's prepare for that possibility
    if (!hasScultorProduct && cart.items?.length > 0) {
      console.log('No sculptor products in cart - cart may be cleared soon');
      // We could add special handling here if needed for pre-cart-clear state
    }
    
    // Refresh slider if needed
    this.updateSliderButtons();
    
    // Update sculptor bundle price if modal is open
    this.updateBundlePriceIfVisible();
    
    // Update sculptor prices in crosssell items
    this.updateSculptorCrosssellPrices();
  }
  
  // Helper method to handle empty cart state
  handleEmptyCart() {
    console.log('Cart is empty, hiding cross-sell component');
    const upsellContainer = this.closest('.cart-drawer__upsell');
    if (upsellContainer) {
      upsellContainer.style.setProperty('display', 'none', 'important');
    }
    
    // If a modal is open, close it
    if (this.modal && this.modal.getAttribute('aria-hidden') === 'false') {
      this.closeModal();
    }
  }
  
  // Properly clean up in disconnectedCallback
  disconnectedCallback() {
    console.log('Cart cross-sell disconnecting - cleaning up resources');
    
    // Clean up event listeners
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    
    if (this.shopifyCartUpdateUnsubscriber) {
      this.shopifyCartUpdateUnsubscriber();
    }
    
    document.removeEventListener('cart:refresh', this.onCartRefresh);
    
    // Clean up slider
    if (this._destroySlider) {
      this._destroySlider();
    }
    
    // Clean up observer
    if (this._sliderObserver) {
      this._sliderObserver.disconnect();
      this._sliderObserver = null;
    }
    
    // Clean up modal event handlers
    if (this._modalEventHandlers) {
      document.removeEventListener('keydown', this._modalEventHandlers.escapeKey);
      this._modalEventHandlers = null;
    }
    
    // Remove modal if it exists
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
  }
  
  setupModal() {
    console.log('Setting up modal');
    
    // If we already have a modal stored in this instance, use it
    if (this.modal && document.body.contains(this.modal)) {
      console.log('Using existing modal');
      // Clear the content but keep the modal
      const contentContainer = this.modal.querySelector('.quick-add-modal__content');
      if (contentContainer) {
        contentContainer.innerHTML = `
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        `;
      }
      return this.modal;
    }
    
    // Check if modal already exists and remove it
    const existingModal = document.querySelector('#quick-add-modal');
    if (existingModal) {
      console.log('Removing existing modal from DOM');
      existingModal.remove();
    }
    
    // Create new modal element
    const modal = document.createElement('div');
    modal.id = 'quick-add-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'quick-add-title');
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.add('quick-add-modal');
    
    // Create content container
    const contentContainer = document.createElement('div');
    contentContainer.classList.add('quick-add-modal__content');
    contentContainer.setAttribute('tabindex', '-1');
    contentContainer.innerHTML = `
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    `;
    
    // Append content container to modal
    modal.appendChild(contentContainer);
    
    // Append modal to body
    document.body.appendChild(modal);
    
    // Store the modal reference
    this.modal = modal;
    
    // Setup event handlers
    this.setupModalCloseHandlers();
    
    // Set up focus trap
    this.setupFocusTrap();
    
    return modal;
  }
  
  setupModalCloseHandlers() {
    // Use the instance modal property if available, otherwise query the DOM
    let modal = this.modal;
    if (!modal) {
      modal = document.querySelector('#quick-add-modal');
    }
    
    if (!modal) {
      console.error('No modal found to set up close handlers');
      return;
    }
    
    // Store handlers for cleanup
    this._modalEventHandlers = this._modalEventHandlers || {};
    
    // Remove any existing handlers to prevent duplicates
    if (this._modalEventHandlers.overlay) {
      modal.removeEventListener('click', this._modalEventHandlers.overlay);
    }
    if (this._modalEventHandlers.escapeKey) {
      document.removeEventListener('keydown', this._modalEventHandlers.escapeKey);
    }
    
    // Handler for close button clicks
    const handleCloseButtonClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Close button clicked');
      this.closeModal();
    };
    
    // Handler for clicking on overlay
    const handleOverlayClick = (e) => {
      if (e.target === modal) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Overlay clicked');
        this.closeModal();
      }
    };
    
    // Handler for escape key
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        console.log('Escape key pressed');
        this.closeModal();
      }
    };
    
    // Add click event listener to modal background for close
    modal.addEventListener('click', handleOverlayClick);
    
    // Add keyboard event listener for escape key
    document.addEventListener('keydown', handleEscapeKey);
    
    // Add handlers to event handlers object for cleanup
    this._modalEventHandlers.closeButton = handleCloseButtonClick;
    this._modalEventHandlers.overlay = handleOverlayClick;
    this._modalEventHandlers.escapeKey = handleEscapeKey;
    
    // Immediately set up any existing close buttons
    const closeButtons = modal.querySelectorAll('.quick-add-modal__close');
    if (closeButtons.length > 0) {
      console.log(`Found ${closeButtons.length} close buttons, setting up handlers`);
      closeButtons.forEach(button => {
        // Remove any existing handlers first
        button.removeEventListener('click', this._modalEventHandlers.closeButton);
        // Add the new handler
        button.addEventListener('click', handleCloseButtonClick);
      });
    } else {
      console.log('No close buttons found, will attempt to set up later');
      
      // Set up a MutationObserver to detect when close buttons are added
      if (!this._closeButtonObserver) {
        this._closeButtonObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              // Check if any close buttons were added
              const newCloseButtons = modal.querySelectorAll('.quick-add-modal__close');
              if (newCloseButtons.length > 0) {
                console.log(`MutationObserver found ${newCloseButtons.length} close buttons`);
                newCloseButtons.forEach(button => {
                  // Ensure we don't add duplicate handlers
                  button.removeEventListener('click', handleCloseButtonClick);
                  button.addEventListener('click', handleCloseButtonClick);
                });
              }
            }
          });
        });
        
        // Start observing the modal for DOM changes
        this._closeButtonObserver.observe(modal, {
          childList: true,
          subtree: true
        });
      }
    }
  }

  setupFocusTrap() {
    const modal = document.querySelector('#quick-add-modal');
    if (!modal) {
      console.error('No modal found to set up focus trap');
      return;
    }
    
    // Find all focusable elements
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    // Function to handle tab key to trap focus
    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;
      
      // If shift + tab and on first element, move to last
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } 
      // If tab and on last element, move to first
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };
    
    // Add event listener
    modal.addEventListener('keydown', handleTabKey);
    
    // Store reference to remove later
    this._focusTrap = { 
      modal,
      handleTabKey,
      dispose: () => {
        modal.removeEventListener('keydown', handleTabKey);
      }
    };
  }
  
  closeModal() {
    console.log('Attempting to close modal');
    
    // Try to find the modal if reference is missing
    let modal = this.modal;
    if (!modal) {
      modal = document.querySelector('#quick-add-modal');
    }
    
    if (!modal) {
      console.error('No modal found to close');
      return;
    }
    
    // Disconnect the observer if it exists
    if (this._closeButtonObserver) {
      console.log('Disconnecting close button observer');
      this._closeButtonObserver.disconnect();
      this._closeButtonObserver = null;
    }

    // First set aria-hidden to true
    modal.setAttribute('aria-hidden', 'true');
    
    // Remove body classes to restore scrolling
    document.body.classList.remove('quick-add-modal-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    
    // Clear the modal content to prevent memory leaks and reset state
    const modalContent = modal.querySelector('.quick-add-modal__content');
    if (modalContent) {
      modalContent.innerHTML = `
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      `;
    }
    
    // Restore focus to last active element before closing the modal completely
    if (this._lastFocusedElement && this._lastFocusedElement.focus) {
      // Delay to ensure proper focus restoration after modal is fully hidden
      setTimeout(() => {
        try {
          this._lastFocusedElement.focus();
        } catch(e) {
          console.error('Error restoring focus:', e);
        }
      }, 10);
    }
  }

  bindQuickAddButtons() {
    this.quickAddButtons = this.querySelectorAll('[data-quick-add-button]');
    if (!this.quickAddButtons || this.quickAddButtons.length === 0) {
      console.log('No quick add buttons found');
      return;
    }
    
    console.log(`Found ${this.quickAddButtons.length} quick add buttons`);
    
    this.quickAddButtons.forEach(button => {
      // Remove any existing event listeners first
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      // Add fresh event listener
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = newButton.dataset.productHandle;
        console.log(`Quick add button clicked for product: ${handle}`);
        this.openQuickAddModal(handle);
      });
    });
  }

  // Helper to generate the loading HTML
  getLoadingHTML() {
    return `
      <div class="loading-container" aria-live="polite">
        <div class="loading-spinner"></div>
        <p>Loading product information...</p>
      </div>
    `;
  }
  
  // Helper to generate the error HTML
  getErrorHTML(message = 'There was an error loading this product. Please try again.') {
    return `
      <div class="error-container" aria-live="assertive">
        <div class="error-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M12 7V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <circle cx="12" cy="16" r="1" fill="currentColor"/>
          </svg>
        </div>
        <p>${message}</p>
      </div>
    `;
  }

  async openQuickAddModal(handle, skipBundleCheck = false) {
    // Prevent multiple rapid calls
    if (this._isOpeningModal) return;
    this._isOpeningModal = true;
    
    try {
      if (!handle) {
        console.error('No product handle provided');
        this._isOpeningModal = false;
        return;
      }
      
      console.log('Quick add button clicked for product:', handle);
      
      // Check if this is the Sculptor product and redirect to bundle modal if needed
      // Normalize handle comparison to be more robust
      const isScultorProduct = handle.toLowerCase().includes('sculptor');
      
      if (!skipBundleCheck && isScultorProduct) {
        const sculptorData = await this.getCartSculptorCount();
        const sculptorCount = sculptorData.sculptorCount || 0;
        
        console.log('Checking for bundle modal:', {
          handle,
          isScultorProduct,
          sculptorCount
        });
        
        // If we already have sculptors in cart but less than 5, use bundle modal
        if (sculptorCount > 0 && sculptorCount < 4) {
          console.log('Redirecting to bundle modal for sculptor product');
          this._isOpeningModal = false; // Reset flag before redirecting
          return this.openSculptorBundleModal(handle);
        }
      }
      
      try {
        // Setup loading state
        this.setupLoadingState();
        
        console.log('Opening regular quick-add modal for:', handle);

        // Fetch product data
        console.log('Fetching product data from URL:', `/products/${handle}.js`);
        const response = await fetch(`/products/${handle}.js`);
        console.log('Response status:', response.status);
        console.log('Response URL:', response.url);
        
        if (!response.ok) throw new Error(`Failed to load product data for ${handle}`);

        // Get the response as text first for debugging
        const responseText = await response.text();
        console.log('Response text (first 100 chars):', responseText.substring(0, 100));
        
        // Manually parse and fix any issues with the data
        let product;
        try {
          product = JSON.parse(responseText);
          console.log('Successfully parsed product JSON');
          
          // Fix image URLs to ensure they have a protocol
          if (product.featured_image && typeof product.featured_image === 'string' && 
              product.featured_image.startsWith('//')) {
            console.log('Fixing protocol-relative featured image URL');
            product.featured_image = 'https:' + product.featured_image;
          }
          
          // Fix image URLs in media array
          if (product.media && Array.isArray(product.media)) {
            product.media.forEach(media => {
              if (media.src && media.src.startsWith('//')) {
                media.src = 'https:' + media.src;
              }
              if (media.preview_image && media.preview_image.src && 
                  media.preview_image.src.startsWith('//')) {
                media.preview_image.src = 'https:' + media.preview_image.src;
              }
            });
            console.log('Fixed media array URLs');
          }
          
          // Fix image URLs in images array
          if (product.images && Array.isArray(product.images)) {
            product.images = product.images.map(img => 
              typeof img === 'string' && img.startsWith('//') ? 'https:' + img : img
            );
            console.log('Fixed images array URLs');
          }
          
          console.log('Product data prepared');
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          throw new Error('Invalid product data format');
        }

        // Update the modal with product data
        console.log('Setting up modal');
        this.setupModal();
        
        console.log('Populating modal with data');
        this.populateModal(product);
        
        console.log('Opening modal');
        this.openModal();
        this.lastActiveElement = document.activeElement; // Store for focus restoration
      } catch (error) {
        console.error('Error in openQuickAddModal:', error);
        if (this.modal && this.modal.querySelector('.quick-add-modal__content')) {
          this.modal.querySelector('.quick-add-modal__content').innerHTML = this.getErrorHTML('Error: ' + error.message);
        } else {
          console.error('Modal or content container not available to display error');
        }
      }
    } finally {
      // Clear the flag with a slight delay to prevent rapid re-triggering
      setTimeout(() => {
        this._isOpeningModal = false;
      }, 300);
    }
  }

  generateQuickAddHTML(product) {
    // Defensive coding to handle potentially malformed product data
    if (!product || typeof product !== 'object') {
      console.error('Invalid product data:', product);
      return this.getErrorHTML('Invalid product data structure');
    }
    
    // Ensure product has variants
    if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      console.error('Product missing variants:', product);
      return this.getErrorHTML('Product data is missing variants');
    }
    
    // Get the first available variant or the first variant
    const firstVariant = product.variants.find(v => v.available) || product.variants[0];
    const hasMultipleVariants = product.variants.length > 1;
    
    // Format price with currency
    const formatMoney = (cents) => {
      return '€' + (cents/100).toFixed(2).replace('.', ',');
    };
    
    // Calculate savings if there's a compare at price
    let savingsHTML = '';
    if (product.compare_at_price > product.price) {
      const savings = product.compare_at_price - product.price;
      savingsHTML = `<span class="save-badge">SAVE €${(savings/100).toFixed(0)}</span>`;
    }
    
    // Safely handle product title with Unicode escape sequences
    const safeTitle = product.title ? product.title.replace(/\\u[\dA-F]{4}/gi, match => {
      try {
        return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
      } catch (e) {
        return match;
      }
    }) : 'Product';
    
    // Safely handle featured image
    let featuredImage = '';
    if (product.featured_image) {
      if (typeof product.featured_image === 'object') {
        featuredImage = product.featured_image.src || '';
      } else {
        featuredImage = product.featured_image;
      }
      
      // Ensure URL has protocol
      if (featuredImage.startsWith('//')) {
        featuredImage = 'https:' + featuredImage;
      }
    }
    
    // Generate thumbnails for product images
    let thumbnailsHTML = '';
    /* Removing thumbnail generation as requested
    if (product.images && Array.isArray(product.images) && product.images.length > 1) {
      thumbnailsHTML = `
        <div class="quick-add-product__thumbnails">
          ${product.images.map((image, index) => {
            // Ensure image URL has protocol
            let imageUrl = '';
            if (typeof image === 'string') {
              imageUrl = image.startsWith('//') ? 'https:' + image : image;
            } else if (image && image.src) {
              imageUrl = image.src.startsWith('//') ? 'https:' + image.src : image.src;
            }
            
            return `
              <button 
                type="button"
                class="thumbnail-button${index === 0 ? ' active' : ''}" 
                data-image-url="${imageUrl}"
                data-index="${index}"
                aria-label="View product image ${index + 1}"
              >
                <img 
                  src="${imageUrl}" 
                  alt="${safeTitle} - Image ${index + 1}"
                >
              </button>
            `;
          }).join('')}
        </div>
      `;
    }
    */
    
    // Create color/variant selectors if needed
    let colorOptionsHTML = '';
    let sizeOptionsHTML = '';
    
    if (hasMultipleVariants) {
      // Find color and size options
      let colorOptionName = '';
      let sizeOptionName = '';
      let colorOptionIndex = -1;
      let sizeOptionIndex = -1;
      
      // Safely check option names for color and size
      if (product.options && Array.isArray(product.options)) {
        product.options.forEach((option, index) => {
          if (typeof option === 'string') {
            const lowerOption = option.toLowerCase();
            if (lowerOption.includes('color') || lowerOption.includes('colour')) {
              colorOptionName = option;
              colorOptionIndex = index;
            } else if (lowerOption.includes('size')) {
              sizeOptionName = option;
              sizeOptionIndex = index;
            }
          } else if (option && option.name) {
            const lowerName = option.name.toLowerCase();
            if (lowerName.includes('color') || lowerName.includes('colour')) {
              colorOptionName = option.name;
              colorOptionIndex = index;
            } else if (lowerName.includes('size')) {
              sizeOptionName = option.name;
              sizeOptionIndex = index;
            }
          }
        });
      }
      
      // Generate color options if available
      if (colorOptionIndex !== -1) {
        const colorValues = [];
        
        // Get unique color values from variants
        product.variants.forEach(variant => {
          if (variant.options && Array.isArray(variant.options) && variant.options[colorOptionIndex]) {
            const colorValue = variant.options[colorOptionIndex];
            if (colorValue && !colorValues.includes(colorValue)) {
              colorValues.push(colorValue);
            }
          }
        });
        
        const selectedColor = colorValues[0] || '';
        
        if (colorValues.length > 0) {
          colorOptionsHTML = `
            <div class="option-selector color-selector">
              <div class="option-label">Color: <span class="selected-value">${selectedColor}</span></div>
              <div class="color-options">
                ${colorValues.map(color => {
                  // Find variant with this color
                  const variantWithColor = product.variants.find(v => 
                    v.options && Array.isArray(v.options) && v.options[colorOptionIndex] === color);
                  
                  // Find image for this variant
                  let imageUrl = '';
                  
                  if (variantWithColor && variantWithColor.featured_image && variantWithColor.featured_image.src) {
                    imageUrl = variantWithColor.featured_image.src;
                    if (imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;
                  } else if (product.images && Array.isArray(product.images)) {
                    // Try to find matching image in product images
                    const variantImage = product.images.find(img => 
                      img.alt && img.alt.toLowerCase().includes(color.toLowerCase()));
                    
                    if (variantImage) {
                      imageUrl = typeof variantImage === 'string' ? variantImage : variantImage.src;
                      if (imageUrl && imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;
                    } else {
                      // Default to product featured image
                      imageUrl = featuredImage;
                    }
                  }
                  
                  return `
                    <div class="color-option ${selectedColor === color ? 'selected' : ''}">
                      <button 
                        type="button" 
                        class="color-swatch" 
                        data-option-index="${colorOptionIndex}" 
                        data-value="${color}"
                        data-image-url="${imageUrl}"
                        style="background-color: ${color.toLowerCase()};"
                      ></button>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }
      }
      
      // Generate size options if available
      if (sizeOptionIndex !== -1) {
        const sizeValues = [];
        
        // Get unique size values from variants
        product.variants.forEach(variant => {
          if (variant.options && Array.isArray(variant.options) && variant.options[sizeOptionIndex]) {
            const sizeValue = variant.options[sizeOptionIndex];
            if (sizeValue && !sizeValues.includes(sizeValue)) {
              sizeValues.push(sizeValue);
            }
          }
        });
        
        const selectedSize = sizeValues[0] || '';
        
        if (sizeValues.length > 0) {
          sizeOptionsHTML = `
            <div class="option-selector size-selector">
              <div class="option-label">Size: <span class="selected-value">${selectedSize}</span></div>

              <div class="CM-sizing-chart-popup-link">
                  <a class="trigger-pop-up btn" href="#">Sizing Chart</a>
              </div>

              <div class="size-options">
                ${sizeValues.map(size => `
                  <button 
                    type="button" 
                    class="size-option ${selectedSize === size ? 'selected' : ''}" 
                    data-option-index="${sizeOptionIndex}" 
                    data-value="${size}"
                  >${size}</button>
                `).join('')}
              </div>
            </div>
          `;
        }
      }
    }
    
    // Compile final HTML for modal
    return `
      <button type="button" class="quick-add-modal__close" aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15 5L5 15M5 5L15 15" stroke="black" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="quick-add-product">
        <div class="quick-add-product__top-section">
          <div class="quick-add-product__gallery">
            <div class="quick-add-product__image">
              <img 
                src="${featuredImage}" 
                alt="${safeTitle}"
                onerror="this.onerror=null; this.src='https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png?v=1530129081'"
              >
            </div>
            ${thumbnailsHTML}
          </div>
          <div class="quick-add-product__title-price">
            <h3 class="quick-add-product__title">${safeTitle}</h3>
            <div class="quick-add-product__price">
              ${product.compare_at_price > product.price 
                ? `<s class="compare-price">${formatMoney(product.compare_at_price)}</s>` 
                : ''}
              <span class="price">${formatMoney(product.price)}</span>
              ${savingsHTML}
            </div>
          </div>
        </div>

        <!-- Product ratings -->
        <div class="product-rating">
          <div class="stars">★★★★★</div>
          <span class="rating-text">Rated 4.8 / 5 based on 200+ reviews</span>
        </div>
        
        <div class="quick-add-product__details">
          <form method="post" action="/cart/add" onsubmit="return false;">
            <input type="hidden" name="id" value="${firstVariant.id}">
            
            <div class="product-form">
              ${colorOptionsHTML}
              ${sizeOptionsHTML}
              
              <button 
                type="submit" 
                class="add-to-cart-button"
                ${!firstVariant.available ? 'disabled' : ''}
              >
                ${firstVariant.available ? 'ADD TO CART' : 'SOLD OUT'}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  setupVariantSelectors(product) {
    console.log('Setting up variant selectors for', product.title);
    if (!this.modal) return;
    
    const form = this.modal.querySelector('form');
    if (!form) return;
    
    const variantIdInput = form.querySelector('input[name="id"]');
    const addButton = form.querySelector('.add-to-cart-button');
    
    if (!variantIdInput || !addButton) {
      console.error('Missing required form elements');
      return;
    }
    
    // Initialize with first variant
    let selectedOptions = {};
    
    // Track which option indices we're dealing with
    const colorOptionButtons = this.modal.querySelectorAll('.color-swatch');
    const sizeOptionButtons = this.modal.querySelectorAll('.size-option');
    
    // Update the product image when a color is selected
    const updateProductImage = (imageUrl, button = null) => {
      const productImage = this.modal.querySelector('.quick-add-product__image img');
      if (productImage && imageUrl) {
        productImage.src = imageUrl;
      }
      
      // Update active state for thumbnails - skipping as thumbnails are removed
      /* 
      if (button) {
        // Clear active state from all thumbnails
        this.modal.querySelectorAll('.thumbnail-button').forEach(thumb => {
          thumb.classList.remove('active');
        });
        
        // Add active state to clicked thumbnail
        button.classList.add('active');
      }
      */
    };
    
    // Set up thumbnails - skipping as thumbnails are removed
    /*
    this.modal.querySelectorAll('.thumbnail-button').forEach(button => {
      button.addEventListener('click', () => {
        const imageUrl = button.dataset.imageUrl;
        if (imageUrl) {
          updateProductImage(imageUrl, button);
        }
      });
    });
    */
    
    // Extract initial selected options
    if (colorOptionButtons.length > 0) {
      const selectedColorButton = Array.from(colorOptionButtons).find(button => 
        button.closest('.color-option').classList.contains('selected')
      );
      
      if (selectedColorButton) {
        const optionIndex = selectedColorButton.dataset.optionIndex;
        const value = selectedColorButton.dataset.value;
        selectedOptions[`option${parseInt(optionIndex) + 1}`] = value;
        
        // Update the color label
        const colorLabel = this.modal.querySelector('.color-selector .selected-value');
        if (colorLabel) {
          colorLabel.textContent = value;
        }
      }
    }
    
    if (sizeOptionButtons.length > 0) {
      const selectedSizeButton = Array.from(sizeOptionButtons).find(button => 
        button.classList.contains('selected')
      );
      
      if (selectedSizeButton) {
        const optionIndex = selectedSizeButton.dataset.optionIndex;
        const value = selectedSizeButton.dataset.value;
        selectedOptions[`option${parseInt(optionIndex) + 1}`] = value;
        
        // Update the size label
        const sizeLabel = this.modal.querySelector('.size-selector .selected-value');
        if (sizeLabel) {
          sizeLabel.textContent = value;
        }
      }
    }
    
    // Set up color option buttons
    colorOptionButtons.forEach(button => {
      button.addEventListener('click', () => {
        const optionIndex = button.dataset.optionIndex;
        const value = button.dataset.value;
        
        // Update selected option
        selectedOptions[`option${parseInt(optionIndex) + 1}`] = value;
        
        // Update active state for color buttons
        colorOptionButtons.forEach(btn => {
          btn.closest('.color-option').classList.remove('selected');
        });
        button.closest('.color-option').classList.add('selected');
        
        // Update the color label
        const colorLabel = this.modal.querySelector('.color-selector .selected-value');
        if (colorLabel) {
          colorLabel.textContent = value;
        }
        
        // Update product image if available
        const imageUrl = button.dataset.imageUrl;
        if (imageUrl) {
          updateProductImage(imageUrl);
        }
        
        // Update the selected variant
        this.updateSelectedVariant(product, selectedOptions, variantIdInput, addButton);
      });
    });
    
    // Set up size option buttons
    sizeOptionButtons.forEach(button => {
      button.addEventListener('click', () => {
        const optionIndex = button.dataset.optionIndex;
        const value = button.dataset.value;
        
        // Update selected option
        selectedOptions[`option${parseInt(optionIndex) + 1}`] = value;
        
        // Update active state
        sizeOptionButtons.forEach(btn => {
          btn.classList.remove('selected');
        });
        button.classList.add('selected');
        
        // Update the size label
        const sizeLabel = this.modal.querySelector('.size-selector .selected-value');
        if (sizeLabel) {
          sizeLabel.textContent = value;
        }
        
        // Update the selected variant
        this.updateSelectedVariant(product, selectedOptions, variantIdInput, addButton);
      });
    });
    
    // Initialize selected variant
    this.updateSelectedVariant(product, selectedOptions, variantIdInput, addButton);
  }

  // Helper method to update cart count bubble in the header
  updateCartCountBubble() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartBubble = document.querySelector('.cart-count-bubble');
        if (cartBubble) {
          const count = cart.item_count || 0;
          cartBubble.textContent = count;
          cartBubble.classList.toggle('hidden', count === 0);
        }
      })
      .catch(error => console.error('Error updating cart count:', error));
  }

  // Helper method to update the selected variant
  updateSelectedVariant(product, selectedOptions, variantIdInput, addButton) {
    console.log('Updating selected variant with options:', selectedOptions);
    
    // Find matching variant
    const matchingVariant = this.findMatchingVariant(product, selectedOptions);
    
    // Update variant input and button state
    if (matchingVariant) {
      console.log('Found matching variant:', matchingVariant.title);
      variantIdInput.value = matchingVariant.id;
      
      // Update button state based on variant availability
      if (matchingVariant.available) {
        addButton.removeAttribute('disabled');
        addButton.textContent = 'ADD TO CART';
        addButton.dataset.state = 'idle';
      } else {
        addButton.setAttribute('disabled', true);
        addButton.textContent = 'SOLD OUT';
        addButton.dataset.state = 'disabled';
      }
    } else {
      console.log('No matching variant found');
      // No matching variant found
      addButton.setAttribute('disabled', true);
      addButton.textContent = 'UNAVAILABLE';
      addButton.dataset.state = 'disabled';
    }
  }

  findMatchingVariant(product, selectedOptions) {
    return product.variants.find(variant => {
      // Check if all selected options match this variant
      const optionMatches = Object.keys(selectedOptions).every(option => {
        const optionIndex = parseInt(option.replace('option', '')) - 1;
        return variant.options[optionIndex] === selectedOptions[option];
      });
      
      return optionMatches;
    });
  }

  async addToCart(form) {
    // Prevent multiple simultaneous add to cart requests
    if (this._isAddingToCart) return;
    this._isAddingToCart = true;
    
    const button = form.querySelector('.add-to-cart-button');
    const originalButtonText = button ? button.textContent : 'ADD TO CART';
    
    try {
      // Update button state
      if (button) {
        button.setAttribute('disabled', true);
        button.textContent = 'ADDING...';
        button.dataset.state = 'adding';
      }
      
      // Create a clean FormData object
      const formData = new FormData(form);

      // Add underscore parameters
      formData.append('properties[_crosssell_item]', 'true');
      formData.append('properties[_source]', 'cart-crosssell');
      
      // Log the form data being sent
      console.log('Adding to cart with data:', Object.fromEntries(formData));
      
      // Use fetch API with explicit headers and timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `Add to cart failed with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Successfully added to cart:', data);
      
      // Update cart count bubble
      this.updateCartCountBubble();
      
      // Update bundle price if applicable
      this.updateBundlePriceIfVisible();
      
      // Refresh cart
      this.refreshCart(true);
      
      // Success! Close the modal immediately
      if (this.modal) {
        // Update button state for visual feedback, but close modal quickly
        if (button) {
          button.textContent = 'ADDED!';
          button.dataset.state = 'added';
        }
        
        // Close the modal after a short delay to show success state
        setTimeout(() => {
          this.closeModal();
          
          // Reset button state after modal is closed
          setTimeout(() => {
            if (button) {
              button.textContent = originalButtonText;
              button.dataset.state = 'idle';
              button.removeAttribute('disabled');
            }
          }, 300);
        }, 800);
      } else {
        // If there's no modal for some reason, just reset the button
        if (button) {
          button.textContent = originalButtonText;
          button.dataset.state = 'idle';
          button.removeAttribute('disabled');
        }
      }

      // Allow add to cart again
      this._isAddingToCart = false;
      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Handle error state
      if (button) {
        const errorMessage = error.message && error.message.includes('sold out') 
          ? 'SOLD OUT' 
          : 'ERROR - TRY AGAIN';
        
        button.textContent = errorMessage;
        button.dataset.state = 'error';
        
        setTimeout(() => {
          button.textContent = originalButtonText;
          button.dataset.state = 'idle';
          button.removeAttribute('disabled');
        }, 2000);
      }
      
      // Allow add to cart again
      this._isAddingToCart = false;
      throw error;
    }
  }
  
  // Add internal parameter to track if this is an internal refresh
  refreshCart(isInternal = false) {
    // Prevent duplicate refreshes
    if (this.isRefreshing) {
      console.log('Refresh already in progress, skipping');
      return;
    }
    
    // Set refreshing flag
    this.isRefreshing = true;
    
    // Set a timeout to reset the flag in case something goes wrong
    const refreshTimeout = setTimeout(() => {
      this.isRefreshing = false;
    }, 5000); // Safety timeout after 5 seconds
    
    console.log('Refreshing cart', isInternal ? '(internal)' : '(external)');
    
    // Use preferred cart update method based on what's available
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      // Use the standard Shopify event system
      if (!isInternal) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-crosssell-internal',
          requestType: 'refresh'
        });
      }
    } else {
      // Fall back to custom event if Shopify's event system isn't available
      if (!isInternal) {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'cart-crosssell-internal' }
        }));
      }
    }
    
    // Try Shopify's cart API first (most reliable)
    if (window.Shopify && window.Shopify.updateCartFromForm) {
      console.log('Using Shopify.updateCartFromForm to refresh cart');
      this.isRefreshing = false;
      clearTimeout(refreshTimeout);
      return;
    }
    
    // Method 1: Find the cart drawer and call the onCartUpdate method
    const cartDrawer = document.querySelector('cart-drawer');
    const cartDrawerItems = document.querySelector('cart-drawer cart-drawer-items');
    
    if (cartDrawer) {
      // Try to open drawer and get fresh cart data
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
      
      if (typeof cartDrawer.getCartData === 'function') {
        cartDrawer.getCartData(true);
      }
    }
    
    // Try to call onCartUpdate method if available
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      cartDrawerItems.onCartUpdate();
      this.isRefreshing = false;
      clearTimeout(refreshTimeout);
      return;
    }
    
    // Method 2: Use fetch to update sections - most reliable fallback
    fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(response => response.json())
      .then(sectionsData => {
        try {
          // Update cart drawer if data available
          if (sectionsData['cart-drawer']) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sectionsData['cart-drawer'];
            
            const cartDrawerElement = document.getElementById('CartDrawer');
            if (cartDrawerElement && tempDiv.querySelector('#CartDrawer')) {
              // Instead of replacing the entire contents, find specific elements to update
              // This helps preserve our event handlers
              
              // First, update the cart items
              const sourceItems = tempDiv.querySelector('#CartDrawer-CartItems');
              const targetItems = cartDrawerElement.querySelector('#CartDrawer-CartItems');
              if (sourceItems && targetItems) {
                targetItems.innerHTML = sourceItems.innerHTML;
              }
              
              // Then update the footer
              const sourceFooter = tempDiv.querySelector('.drawer__footer');
              const targetFooter = cartDrawerElement.querySelector('.drawer__footer');
              if (sourceFooter && targetFooter) {
                targetFooter.innerHTML = sourceFooter.innerHTML;
              }
            }
          }
          
          // Update cart icon bubble
          if (sectionsData['cart-icon-bubble']) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sectionsData['cart-icon-bubble'];
            
            const cartIconElement = document.getElementById('cart-icon-bubble');
            if (cartIconElement && tempDiv.querySelector('#cart-icon-bubble')) {
              cartIconElement.innerHTML = tempDiv.querySelector('#cart-icon-bubble').innerHTML;
            }
          }
          
          // After cart updates, update the sculptor crosssell prices
          setTimeout(() => {
            this.updateSculptorCrosssellPrices();
          }, 300);
        } catch (e) {
          console.error('Error parsing section data:', e);
        } finally {
          // Reset refreshing flag
          this.isRefreshing = false;
          clearTimeout(refreshTimeout);
        }
      })
      .catch(e => {
        console.error('Error updating cart sections:', e);
        // Reset refreshing flag even on error
        this.isRefreshing = false;
        clearTimeout(refreshTimeout);
      });
  }

  // Utility function for debouncing expensive operations
  debounce(fn, wait) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(context, args), wait);
    };
  }

  // Handle direct navigation to a specific slide (used by pagination dots)
  goToSlide(index) {
    if (!this.container) return;
    
    // Calculate the total scrollable width
    const containerWidth = this.container.clientWidth;
    const totalScrollWidth = this.container.scrollWidth - containerWidth;
    const totalItems = this.maxIndex + 1;
    
    // Calculate the scroll position based on the index
    // We convert index to a percentage of the total scroll width
    const scrollPercentage = totalItems > 1 ? index / (totalItems - 1) : 0;
    const scrollTarget = totalScrollWidth * scrollPercentage;
    
    // Scroll to the position
    this.container.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    });
    
    // Update the active dot
    this.updateActiveDot(index);
  }

  // Get the count of Sculptor-2-0 products in the cart
  async getCartSculptorCount() {
    try {
      console.log('Fetching cart data to check for sculptors');
      
      // Fetch the current cart
      const response = await fetch('/cart.js');
      if (!response.ok) {
        console.error('Failed to fetch cart data:', response.status);
        return { sculptorCount: 0, sculptorTotalPrice: '$0' };
      }
      
      const cart = await response.json();
      
      // Define sculptor product handles to look for
      const SCULPTOR_HANDLES = ['sculptor-2-0', 'sculptor-2-0-copy'];
      
      // Count sculptors in cart
      let sculptorCount = 0;
      let sculptorTotalPrice = 0;
      
      // Check each item in the cart
      if (cart.items && Array.isArray(cart.items)) {
        cart.items.forEach(item => {
          // Check if this is a sculptor product
          if (item.handle && SCULPTOR_HANDLES.includes(item.handle)) {
            sculptorCount += item.quantity;
            sculptorTotalPrice += item.line_price;
          }
        });
      }
      
      // Format the price
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(sculptorTotalPrice / 100);
      
      console.log('DEBUG - getCartSculptorCount:', {
        sculptorCount,
        sculptorTotalPrice: formattedPrice,
        cartItemCount: cart.item_count,
        cartTotalPrice: cart.total_price
      });
      
      return {
        sculptorCount,
        sculptorTotalPrice: formattedPrice
      };
    } catch (error) {
      console.error('Error in getCartSculptorCount:', error);
      // Return default values in case of error
      return { sculptorCount: 0, sculptorTotalPrice: '$0' };
    }
  }
  
  // Open the Sculptor bundle modal
  async openSculptorBundleModal(handle) {
    // Prevent multiple rapid calls
    if (this._isOpeningBundleModal) return;
    this._isOpeningBundleModal = true;
    
    try {
      console.log('Opening Sculptor bundle modal');
      
      // Setup loading state
      this.setupLoadingState();
      this.openModal();
      
      // Fetch product data first
      console.log('Fetching product data for', handle);
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`Failed to load product data for ${handle}: ${response.status}`);
      }
      const product = await response.json();
      console.log('Product data received', product.title);
      
      // Get cart data to determine quantity needed - do this AFTER product fetch to minimize race conditions
      console.log('Checking current cart for sculptors');
      const sculptorData = await this.getCartSculptorCount();
      const sculptorCount = sculptorData.sculptorCount || 0;
      const quantityNeeded = Math.max(1, 4 - sculptorCount);
      
      // Store for later use
      this._sculptorQuantityNeeded = quantityNeeded;
      
      console.log(`Sculptor bundle modal: ${sculptorCount} in cart, ${quantityNeeded} needed to complete bundle`);
      
      // Generate HTML for the modal
      console.log('Generating bundle HTML');
      this.generateBundleHTML(product, quantityNeeded);
      
      // Wait for the HTML to be rendered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if modal still exists (it might have been closed or removed)
      const modal = document.querySelector('#quick-add-modal');
      if (!modal) {
        console.warn('Modal no longer exists, aborting setup');
        return;
      }
      
      // Set up the selectors and update the price
      console.log('Setting up bundle selectors and price');
      this.setupBundleSelectors(product, product.title, quantityNeeded);
      
      // Update price separately with error handling
      try {
        this.updateBundlePrice(product, quantityNeeded);
      } catch (priceError) {
        console.error('Error updating bundle price:', priceError);
        // Continue with the modal even if price update fails
      }
    } catch (error) {
      console.error('Error in openSculptorBundleModal:', error);
      this.showErrorInModal('Could not load the bundle. Please try again.');
    } finally {
      // Clear the flag after a delay to prevent rapid re-triggering
      setTimeout(() => {
        this._isOpeningBundleModal = false;
      }, 300);
    }
  }
  
  // Helper method to show error in modal
  showErrorInModal(message) {
    try {
      const modal = document.querySelector('#quick-add-modal');
      if (!modal) {
        console.error('No modal found to show error');
        return;
      }
      
      const content = modal.querySelector('.quick-add-modal__content');
      if (!content) {
        console.error('No modal content found to show error');
        
        // Try to create content container if it doesn't exist
        const newContent = document.createElement('div');
        newContent.classList.add('quick-add-modal__content');
        newContent.setAttribute('tabindex', '-1');
        modal.appendChild(newContent);
        
        // Use the newly created content
        this.showErrorInModal(message);
        return;
      }
      
      content.innerHTML = `
        <div class="error-container" aria-live="assertive">
          <div class="error-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
              <path d="M12 7V13" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
              <circle cx="12" cy="16" r="1" fill="currentColor"></circle>
            </svg>
          </div>
          <p>${message}</p>
          <button type="button" class="add-to-cart-button" onclick="window.location.reload()">
            Refresh Page
          </button>
        </div>
      `;
      
      // Make sure modal is visible
      this.openModal();
      
      // Add event listener to the refresh button
      const refreshButton = content.querySelector('.add-to-cart-button');
      if (refreshButton) {
        refreshButton.addEventListener('click', () => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.error('Error showing error in modal:', error);
      // As a last resort, try to alert the user
      alert(`Error: ${message}\n\nPlease refresh the page and try again.`);
    }
  }
  
  // Generate bundle HTML directly if section fetch fails
  async generateBundleHTML(product, quantityNeeded) {
    try {
      const modal = document.querySelector('#quick-add-modal');
      if (!modal) {
        console.error('Modal not found');
        return;
      }
      
      const modalContent = modal.querySelector('.quick-add-modal__content');
      if (!modalContent) {
        console.error('Modal content not found');
        return;
      }
      
      // Format money function for price display
      const formatMoney = (cents) => {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return formatter.format(cents / 100);
      };
      
      // Get the bundle completion price
      const unitPrice = product.price;
      // Use the bundle pricing constants for consistency
      // const BUNDLE_PRICE = 7999; // $79.99 in cents for 5 items
      // const singlePrice = product.price || 3999;
      const BUNDLE_PRICE = 6990; // $79.99 in cents for 5 items
      const singlePrice = product.price || 3495;
      
      // If we need multiple items, calculate a special price based on bundle logic
      // rather than just multiplying the single price
      let totalPrice;
      if (quantityNeeded > 0) {
        // Calculate remaining amount to complete the bundle
        const sculptorData = this.getCartSculptorCount ? 
          await this.getCartSculptorCount() : 
          { sculptorCount: 0, sculptorTotalPrice: '$0' };
          
        const sculptorCount = sculptorData.sculptorCount || 0;
        const sculptorTotalPrice = sculptorData.sculptorTotalPrice || '$0';
        const sculptorTotalAmount = parseFloat(sculptorTotalPrice.replace(/[^0-9.-]+/g, '')) * 100;
        
        console.log('Bundle modal pricing - Sculptor count:', sculptorCount, 
                   'Amount:', (sculptorTotalAmount/100).toFixed(2),
                   'Quantity needed:', quantityNeeded);
        
        // Calculate bundle pricing based on what's already in cart
        if (sculptorCount > 0) {
          // Calculate remaining amount to complete bundle
          totalPrice = BUNDLE_PRICE - sculptorTotalAmount;
          // Ensure minimum pricing
          totalPrice = Math.max(totalPrice, 500 * quantityNeeded); // Min $5 per item
        } else {
          // No items in cart, use the bundle price
          totalPrice = BUNDLE_PRICE;
        }
      } else {
        // Just one item, use the regular price
        totalPrice = singlePrice;
      }
      
      // Calculate the savings (if any)
      const regularPrice = product.compare_at_price || product.price;
      const totalRegularPrice = regularPrice * quantityNeeded;
      const savingsAmount = totalRegularPrice - totalPrice;
      const savingsPercent = Math.round((savingsAmount / totalRegularPrice) * 100);
      
      // In the generateBundleHTML function, modify the colorSelectorsHTML generation:
      let colorSelectorsHTML = '';
      for (let i = 0; i < quantityNeeded; i++) {
        colorSelectorsHTML += `
          <div class="option-selector">
            <label class="option-label">SCULPTOR™ ${i + 1}: <span class="selected-color-name" data-selector-index="${i}"></span></label>
            <div class="color-options" data-selector-index="${i}">
              ${this.generateColorSwatches(product, i)}
            </div>
          </div>
        `;
      }
      
      // Get size options
      let sizeOptionsHTML = '';
      
      // Find size option - check both options_with_values and options arrays
      let sizeValues = [];
      
      // First try options_with_values (Shopify product object)
      if (product.options_with_values) {
        const sizeOption = product.options_with_values.find(option => 
          option.name.toLowerCase().includes('size')
        );
        if (sizeOption && sizeOption.values) {
          sizeValues = sizeOption.values;
        }
      }
      
      // If no size values found, try extracting from options and variants
      if (sizeValues.length === 0 && product.options) {
        // Find the index of the size option
        let sizeOptionIndex = -1;
        product.options.forEach((option, index) => {
          const optionName = typeof option === 'string' ? option : option.name;
          if (optionName.toLowerCase().includes('size')) {
            sizeOptionIndex = index;
          }
        });
        
        // Extract unique size values from variants
        if (sizeOptionIndex !== -1 && product.variants && product.variants.length > 0) {
          const uniqueSizes = new Set();
          product.variants.forEach(variant => {
            if (variant.options && variant.options[sizeOptionIndex]) {
              uniqueSizes.add(variant.options[sizeOptionIndex]);
            }
          });
          sizeValues = Array.from(uniqueSizes);
        }
      }
      
      // Generate size options HTML
      if (sizeValues.length > 0) {
        sizeOptionsHTML = `
          <div class="size-options">
            ${sizeValues.map(value => 
              `<button type="button" class="size-option" data-value="${value}">${value}</button>`
            ).join('')}
          </div>
        `;
      } else {
        console.warn('No size options found for product');
        sizeOptionsHTML = '<p>No size options available</p>';
      }
      
      const bundleHTML = `
        <button type="button" class="quick-add-modal__close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="black" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="quick-add-product">
          <div class="quick-add-product__top-section">
            <div class="quick-add-product__gallery">
              <div class="quick-add-product__image">
                <img src="${product.featured_image}" alt="${product.title}">
              </div>
            </div>
            <div class="quick-add-product__title-price">
              <h3 class="quick-add-product__title">${quantityNeeded > 1 ? `Complete Your 4-Pack` : product.title}</h3>
              
              <span class="free-item" style="/* display: none; */font-size: 0.9em;font-weight: bold;margin-top: -7px;color: rgb(var(--color-foreground));">+ FREE SCULPTSHIRT™</span>
              
              <div class="quick-add-product__price" id="bundle-price">
                ${quantityNeeded > 1 ? 
                  `<span class="price">${formatMoney(totalPrice)} for ${quantityNeeded} more</span>` : 
                  `<span class="price">${formatMoney(totalPrice)}</span>`
                }
                ${savingsAmount > 0 ? 
                  `<span class="save-badge">Save ${savingsPercent}%</span>` : 
                  ''
                }
              </div>
            </div>
          </div>

          <!-- Product ratings -->
          <div class="product-rating">
            <div class="stars">★★★★★</div>
            <span class="rating-text">Rated 4.8 / 5 based on 200+ reviews</span>
          </div>

          <div class="quick-add-product__details">
            <form class="product-form">
              <div class="option-selector">
                <label class="option-label">Size</label>

                <div class="CM-sizing-chart-popup-link">
                  <a class="trigger-pop-up btn" href="#">Sizing Chart</a>
                </div>

                ${sizeOptionsHTML}
              </div>
              
              ${colorSelectorsHTML}
              
              <button type="button" class="add-to-cart-button" disabled>
                ${quantityNeeded > 1 ? 'Add Bundle to Cart' : 'Add to Cart'}
              </button>
            </form>
          </div>
        </div>
      `;
      
      modalContent.innerHTML = bundleHTML;
      
      // Add event listener for close button
      const closeButton = modalContent.querySelector('.quick-add-modal__close');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeModal());
      }
    } catch (error) {
      console.error('Error in generateBundleHTML:', error);
      this.showErrorInModal('Error loading product information. Please try again.');
    }
  }
  
  // Generate color swatches for the template
  generateColorSwatches(product, selectorIndex = 0) {
    // Find the color option position
    let colorOptionIndex = -1;
    
    // Check for color options in product
    product.options.forEach((option, index) => {
      const optionName = typeof option === 'string' ? option : option.name;
      if (optionName.toLowerCase().includes('color')) {
        colorOptionIndex = index;
      }
    });
    
    if (colorOptionIndex === -1) {
      console.warn('No color option found for product');
      return `<div class="no-colors-available">No color options available</div>`;
    }
    
    // Get unique color values from variants
    const colorValues = new Set();
    const colorMap = new Map(); // Map colors to their corresponding variant image
    
    product.variants.forEach(variant => {
      if (variant.available) {
        const colorValue = variant.options[colorOptionIndex];
        if (colorValue) {
          colorValues.add(colorValue);
          
          // Store the image URL if available
          if (variant.featured_image && variant.featured_image.src) {
            colorMap.set(colorValue, variant.featured_image.src);
          }
        }
      }
    });
    
    // Convert set to array
    const uniqueColors = Array.from(colorValues);
    
    // Function to generate CSS style for a color
    const getColorStyle = (colorName, imageUrl) => {
      // If we have an image for this color, use it as background
      if (imageUrl) {
        return `background-image: url('${imageUrl}'); background-size: cover; background-position: center;`;
      }
      
      // Standard color mapping for common color names
      const colorLower = colorName.toLowerCase();
      const colorMapping = {
        'black': '#000000',
        'white': '#FFFFFF',
        'red': '#FF0000',
        'blue': '#0000FF',
        'green': '#008000',
        'yellow': '#FFFF00',
        'purple': '#800080',
        'orange': '#FFA500',
        'navy': '#000080',
        'navy blue': '#000080',
        'grey': '#808080',
        'gray': '#808080',
        'brown': '#A52A2A',
        'charcoal': '#36454F',
        'khaki': '#C3B091',
        'tan': '#D2B48C',
        'olive': '#808000',
        'burgundy': '#800020',
        'beige': '#F5F5DC',
        'teal': '#008080',
        'cream': '#FFFDD0'
      };
      
      // Handle special colors with gradients
      if (colorLower.includes('rose gold')) return 'background: linear-gradient(to right, #e0bfb8, #d4a59a)';
      if (colorLower.includes('gold')) return 'background: linear-gradient(to right, #d4af37, #f4cd26)';
      if (colorLower.includes('silver')) return 'background: linear-gradient(to right, #C0C0C0, #E8E8E8)';
      if (colorLower.includes('rainbow')) return 'background: linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet)';
      
      // Check our mapping for exact or partial matches
      for (const [key, value] of Object.entries(colorMapping)) {
        if (colorLower === key || colorLower.includes(key)) {
          return `background-color: ${value};`;
        }
      }
      
      // Default fallback: try to use the color name as a CSS color
      console.log('Color not in mapping, using direct name:', colorName);
      return `background-color: ${colorLower};`;
    };
    
    // Generate color swatches
    let swatchesHTML = '';
    uniqueColors.forEach((color, index) => {
      const isFirst = index === 0;
      const imageUrl = colorMap.get(color) || '';
      
      swatchesHTML += `
        <button type="button" 
          class="color-option ${isFirst ? 'selected' : ''}" 
          data-value="${color}" 
          data-image-url="${imageUrl}"
          aria-label="${color}">
          <span class="color-swatch" style="${getColorStyle(color, imageUrl)}"></span>
        </button>
      `;
    });
    
    return swatchesHTML || `<div class="no-colors-available">No color options available</div>`;
  }
  
  // Set up bundle selectors
  setupBundleSelectors(product, productTitle, quantityNeeded) {
    console.log(`Setting up bundle selectors for ${quantityNeeded} items`);
    
    try {
      // Wait a bit longer to ensure DOM is fully rendered
      setTimeout(async () => {
        try {
          // Get fresh cart data to ensure we have the latest state
          const sculptorData = await this.getCartSculptorCount();
          const sculptorCount = sculptorData.sculptorCount || 0;
          
          // Recalculate quantity needed based on latest cart data
          const recalculatedQuantityNeeded = Math.max(1, 4 - sculptorCount);
          
          // Use the recalculated quantity if it differs from the passed in value
          if (recalculatedQuantityNeeded !== quantityNeeded) {
            console.log(`Cart state changed: updating quantity needed from ${quantityNeeded} to ${recalculatedQuantityNeeded}`);
            quantityNeeded = recalculatedQuantityNeeded;
            
            // If the quantity changed significantly, we may need to regenerate the HTML
            if (Math.abs(recalculatedQuantityNeeded - quantityNeeded) > 1) {
              console.log('Significant quantity change detected, regenerating bundle HTML');
              this.generateBundleHTML(product, quantityNeeded);
              // Wait for HTML to update
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
          
          // Safely get elements, with null checks at each step
          const modal = document.querySelector('#quick-add-modal');
          if (!modal) {
            console.error('Modal not found for bundle selectors');
            return;
          }
          
          console.log('Modal found, searching for selectors');
          
          const sizeSelector = modal.querySelector('.size-options');
          const colorSelectors = Array.from(modal.querySelectorAll('.color-options'));
          const addButton = modal.querySelector('.add-to-cart-button');
          
          console.log('Element check:', {
            hasSizeSelector: !!sizeSelector,
            colorSelectorCount: colorSelectors.length,
            hasAddButton: !!addButton,
            modalHTML: modal.innerHTML.substring(0, 100) + '...' // Log first 100 chars for debugging
          });
          
          if (!sizeSelector || !colorSelectors.length || !addButton) {
            console.error('Required elements not found in modal', {
              sizeSelector: !!sizeSelector,
              colorSelectors: colorSelectors.length,
              addButton: !!addButton
            });
            
            // If we're missing elements but the modal exists, show an error
            if (modal) {
              this.showErrorInModal('Could not initialize bundle options. Please try again.');
            }
            return;
          }
          
          // Create arrays to track selected options
          let selectedSize = null;
          let selectedColors = Array(quantityNeeded).fill(null);
          
          // Setup size options
          const sizeOptions = sizeSelector.querySelectorAll('.size-option');
          if (sizeOptions.length > 0) {
            // Select the first size by default
            const firstSizeOption = sizeOptions[0];
            firstSizeOption.classList.add('selected');
            selectedSize = firstSizeOption.getAttribute('data-value');
            
            sizeOptions.forEach(option => {
              option.addEventListener('click', () => {
                // Remove selection from all size options
                sizeOptions.forEach(opt => opt.classList.remove('selected'));
                
                // Add selection to clicked option
                option.classList.add('selected');
                
                // Update selected size
                selectedSize = option.getAttribute('data-value');
                console.log(`Selected size: ${selectedSize}`);
                
                // Update button state
                this.updateBundleButtonState(selectedSize, selectedColors, addButton, quantityNeeded);
              });
            });
          }
          
          // Setup color options for each selector
          colorSelectors.forEach((selector, selectorIndex) => {
            if (!selector) return; // Skip if selector is null
            
            const colorOptions = selector.querySelectorAll('.color-option');
            if (!colorOptions.length) {
              console.warn(`No color options found for selector ${selectorIndex}`);
              return;
            }
            
            // Find the label element for this selector
            const selectorContainer = selector.closest('.option-selector');
            const labelElement = selectorContainer ? selectorContainer.querySelector('.option-label') : null;
            
            // Get the base label text without the color
            let baseLabelText = '';
            if (labelElement) {
              baseLabelText = labelElement.textContent.split(':')[0].trim();
            }
            
            // Select first color by default
            const firstColorOption = colorOptions[0];
            firstColorOption.classList.add('selected');
            selectedColors[selectorIndex] = firstColorOption.getAttribute('data-value');
            
            // Update the label to include the selected color
            if (labelElement && baseLabelText) {
              labelElement.innerHTML = `${baseLabelText}: <span class="selected-color-value">${selectedColors[selectorIndex]}</span>`;
            }
            
            // Remove any existing selected color display elements
            const selectedColorDisplay = selectorContainer?.querySelector('.selected-color-display');
            if (selectedColorDisplay) {
              selectedColorDisplay.remove();
            }
            
            colorOptions.forEach(option => {
              option.addEventListener('click', () => {
                // Check if elements still exist in DOM
                if (!document.body.contains(option)) {
                  console.warn('Color option no longer in DOM, ignoring click');
                  return;
                }
                
                // Find the parent selector of this option
                const parentSelector = option.closest('.color-options');
                if (!parentSelector) return;
                
                // Find the index of this selector
                const currentSelectorIndex = colorSelectors.findIndex(sel => sel === parentSelector);
                if (currentSelectorIndex === -1) return;
                
                // Remove selection from all options in this selector
                const allOptionsInThisSelector = parentSelector.querySelectorAll('.color-option');
                allOptionsInThisSelector.forEach(opt => opt.classList.remove('selected'));
                
                // Add selection to clicked option
                option.classList.add('selected');
                
                // Update selected color for this selector
                selectedColors[currentSelectorIndex] = option.getAttribute('data-value');
                console.log(`Selected color for selector ${currentSelectorIndex}: ${selectedColors[currentSelectorIndex]}`);
                
                // Update the label to include the newly selected color
                const selectorContainer = parentSelector.closest('.option-selector');
                const labelElement = selectorContainer ? selectorContainer.querySelector('.option-label') : null;
                
                if (labelElement) {
                  const baseLabelText = labelElement.textContent.split(':')[0].trim();
                  labelElement.innerHTML = `${baseLabelText}: <span class="selected-color-value">${selectedColors[currentSelectorIndex]}</span>`;
                }
                
                // Update button state
                this.updateBundleButtonState(selectedSize, selectedColors, addButton, quantityNeeded);
              });
            });
          });
          
          // Enable button if we have defaults selected
          this.updateBundleButtonState(selectedSize, selectedColors, addButton, quantityNeeded);
          
          // Setup add button
          if (addButton) {
            addButton.addEventListener('click', async (e) => {
              e.preventDefault();
              
              // Check if button still exists in DOM
              if (!document.body.contains(addButton)) {
                console.warn('Add button no longer in DOM, ignoring click');
                return;
              }
              
              try {
                // Disable button and show loading state
                addButton.disabled = true;
                addButton.dataset.state = 'adding';
                addButton.textContent = 'Adding...';
                
                // Get fresh cart data before adding to cart
                const latestSculptorData = await this.getCartSculptorCount();
                const latestSculptorCount = latestSculptorData.sculptorCount || 0;
                
                // If cart state has changed dramatically, show a message and refresh
                if (latestSculptorCount !== sculptorCount) {
                  console.warn(`Cart state changed during add process: ${sculptorCount} -> ${latestSculptorCount}`);
                  addButton.dataset.state = 'error';
                  addButton.textContent = 'Cart Changed - Try Again';
                  addButton.disabled = false;
                  
                  // Refresh the page if cart state is very different
                  if (Math.abs(latestSculptorCount - sculptorCount) > 1) {
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  }
                  return;
                }
                
                // Add bundle to cart
                await this.addBundleToCart(product, selectedSize, selectedColors);
                
                // Show success state
                addButton.dataset.state = 'added';
                addButton.textContent = 'Added to Cart!';
                
                // Close modal after delay
                setTimeout(() => {
                  this.closeModal();
                }, 800);
              } catch (error) {
                console.error('Error adding bundle to cart:', error);
                
                // Show error state
                addButton.dataset.state = 'error';
                addButton.textContent = 'Error - Try Again';
                addButton.disabled = false;
              }
            });
          }
        } catch (innerError) {
          console.error('Error in setupBundleSelectors inner function:', innerError);
          this.showErrorInModal('Could not set up bundle options. Please try again.');
        }
      }, 200); // Increase timeout to ensure DOM is ready
    } catch (error) {
      console.error('Error in setupBundleSelectors:', error);
    }
  }
  
  // Update bundle button state based on selections
  updateBundleButtonState(selectedSize, selectedColors, addButton, quantityNeeded) {
    if (!addButton) return;
    
    console.log('Updating bundle button state:', { size: selectedSize, colors: selectedColors, needed: quantityNeeded });
    
    // Check if all required selections are made
    const hasSizeSelected = !!selectedSize;
    const hasAllColorsSelected = selectedColors.filter(color => !!color).length === quantityNeeded;
    
    const isReady = hasSizeSelected && hasAllColorsSelected;
    
    // Update button state
    addButton.disabled = !isReady;
    
    // Update button text based on state
    if (isReady) {
      addButton.textContent = quantityNeeded > 1 ? `Add ${quantityNeeded} to Cart` : 'Add to Cart';
    } else {
      // Show what's missing
      if (!hasSizeSelected && !hasAllColorsSelected) {
        addButton.textContent = 'Select Size & Colors';
      } else if (!hasSizeSelected) {
        addButton.textContent = 'Select Size';
      } else {
        addButton.textContent = 'Select All Colors';
      }
    }
  }
  
  // Find variant ID for a specific size and color combination
  findVariantId(product, size, color) {
    console.log(`Finding variant ID for size: ${size}, color: ${color}`);
    
    if (!product || !product.variants || !Array.isArray(product.variants)) {
      console.error('Invalid product data for variant search');
      return null;
    }
    
    // Find option indexes
    let sizeOptionIndex = -1;
    let colorOptionIndex = -1;
    
    // Find size and color option indexes
    product.options.forEach((option, index) => {
      const optionName = typeof option === 'string' ? option : option.name;
      const optionNameLower = optionName.toLowerCase();
      
      if (optionNameLower.includes('size')) {
        sizeOptionIndex = index;
      } else if (optionNameLower.includes('color')) {
        colorOptionIndex = index;
      }
    });
    
    console.log(`Option indexes - Size: ${sizeOptionIndex}, Color: ${colorOptionIndex}`);
    
    if (sizeOptionIndex === -1 || colorOptionIndex === -1) {
      console.warn('Could not find required option indexes');
    }
    
    // Try exact match first
    const exactMatch = product.variants.find(variant => {
      if (!variant.options) return false;
      
      const variantSize = sizeOptionIndex >= 0 ? variant.options[sizeOptionIndex] : null;
      const variantColor = colorOptionIndex >= 0 ? variant.options[colorOptionIndex] : null;
      
      return (
        (!size || variantSize === size) && 
        (!color || variantColor === color)
      );
    });
    
    if (exactMatch) {
      console.log(`Found exact match variant: ${exactMatch.id}`);
      return exactMatch.id;
    }
    
    // Try to find a close match by prioritizing color match
    const colorMatch = product.variants.find(variant => {
      if (!variant.options) return false;
      const variantColor = colorOptionIndex >= 0 ? variant.options[colorOptionIndex] : null;
      return variantColor === color;
    });
    
    if (colorMatch) {
      console.log(`Found color match variant: ${colorMatch.id}`);
      return colorMatch.id;
    }
    
    // Fallback to first available variant
    const firstAvailable = product.variants.find(variant => variant.available);
    if (firstAvailable) {
      console.log(`Falling back to first available variant: ${firstAvailable.id}`);
      return firstAvailable.id;
    }
    
    // Last resort: first variant
    if (product.variants.length > 0) {
      console.log(`Last resort: using first variant: ${product.variants[0].id}`);
      return product.variants[0].id;
    }
    
    console.error('No suitable variant found');
    return null;
  }

  // Setup loading state
  setupLoadingState() {
    // Create modal if it doesn't exist
    this.setupModal();
    
    const modal = document.querySelector('#quick-add-modal');
    if (!modal) {
      console.error('No modal found for loading state');
      return;
    }
    
    const contentContainer = modal.querySelector('.quick-add-modal__content');
    if (!contentContainer) {
      console.error('No content container found for loading state');
      return;
    }
    
    // Set loading state
    contentContainer.innerHTML = `
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    `;
    
    // Open the modal to show loading state
    this.openModal();
  }
  
  // Populate modal with product data from server content or generate directly
  populateModal(product, quantityNeeded = 1) {
    console.log('Populating modal with product data');
    
    if (!this.modal) {
      console.error('No modal reference found in populateModal');
      return;
    }
    
    if (!product) {
      console.error('No product data provided to populateModal');
      const modalContent = this.modal.querySelector('.quick-add-modal__content');
      if (modalContent) {
        modalContent.innerHTML = this.getErrorHTML('No product data available');
      }
      return;
    }
    
    // Generate HTML for the quick add modal
    console.log('Generating HTML for modal');
    const modalHTML = this.generateQuickAddHTML(product);
    console.log('HTML generated successfully');
    
    // Update the modal content
    const modalContent = this.modal.querySelector('.quick-add-modal__content');
    if (modalContent) {
      modalContent.innerHTML = modalHTML;
      
      // Set up form handlers
      this.setupFormHandlers(product);
      console.log('Form handlers set up');
      
      // Immediately set up close button handler for the newly added close button
      const closeButton = this.modal.querySelector('.quick-add-modal__close');
      if (closeButton) {
        console.log('Setting up close button handler');
        closeButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
        });
      } else {
        console.warn('No close button found after populating modal');
      }
    }
  }
  
  // Add a dedicated method to set up form handlers
  setupFormHandlers(product) {
    // Find the form in the modal
    const form = this.modal.querySelector('form');
    if (!form) {
      console.error('No form found in modal');
      return;
    }
    
    // Prevent the default form submission
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      console.log('Form submission intercepted');
      this.addToCart(form);
    });
    
    // Also add a click handler for the submit button as a fallback
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.addEventListener('click', (e) => {
        // Only handle if it's not already being handled by form submission
        if (e.defaultPrevented) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        console.log('Submit button click intercepted');
        this.addToCart(form);
      });
    }
    
    // Set up variant selectors
    this.setupVariantSelectors(product);
  }
  
  // Open the modal
  openModal() {
    console.log('Opening modal');
    
    // Try to use the stored modal reference first
    let modal = this.modal;
    
    // If no stored reference, try to find it in the DOM
    if (!modal) {
      console.log('No stored modal reference, looking in DOM');
      modal = document.querySelector('#quick-add-modal');
    }
    
    if (!modal) {
      console.error('No modal found to open');
      return;
    }
    
    // Store reference to previously focused element for returning focus later
    this._lastFocusedElement = document.activeElement;
    
    // Check if modal is already open to avoid duplicate operations
    if (modal.getAttribute('aria-hidden') === 'false') {
      console.log('Modal is already open, skipping');
      return;
    }
    
    // Add body class to prevent scrolling before making modal visible
    // This prevents potential layout shifts
    document.body.classList.add('quick-add-modal-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    
    // Make modal visible
    modal.setAttribute('aria-hidden', 'false');
    console.log('Modal visibility set to visible');
    
    // Set focus to the first focusable element in the modal
    setTimeout(() => {
      // Find all focusable elements
      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length) {
        focusableElements[0].focus();
      } else {
        // If no focusable elements, focus the modal content
        const modalContent = modal.querySelector('.quick-add-modal__content');
        if (modalContent) {
          modalContent.focus();
        } else {
          // As a last resort, focus the modal itself
          modal.setAttribute('tabindex', '-1');
          modal.focus();
        }
      }
    }, 100);
  }

  // New method to update bundle price if the modal is open
  async updateBundlePriceIfVisible() {
    // Check if the modal is open and visible
    if (!this.modal || this.modal.getAttribute('aria-hidden') === 'true') {
      return; // Modal not visible, no need to update
    }
    
    // Check if this is a bundle modal by looking for bundle elements
    const bundleTitle = this.modal.querySelector('.quick-add-product__title');
    if (!bundleTitle || !bundleTitle.textContent.includes('Bundle')) {
      return; // Not a bundle modal
    }
    
    try {
      // Get current sculptor count
      const sculptorData = await this.getCartSculptorCount();
      const TARGET_COUNT = 4;
      const sculptorCount = sculptorData.sculptorCount || 0;
      
      // Recalculate quantity needed based on latest cart data
      const recalculatedQuantityNeeded = Math.max(1, 4 - sculptorCount);
      
      // Use the recalculated quantity if it differs from the passed in value
      if (recalculatedQuantityNeeded !== quantityNeeded) {
        console.log(`Cart state changed: updating quantity needed from ${quantityNeeded} to ${recalculatedQuantityNeeded}`);
        quantityNeeded = recalculatedQuantityNeeded;
      }
      
      // Get price data
      const unitPrice = product.price;
      const unitCompareAtPrice = product.compare_at_price || unitPrice;
      const totalPrice = unitPrice * quantityNeeded;
      const totalCompareAtPrice = unitCompareAtPrice * quantityNeeded;
      const savingsAmount = totalCompareAtPrice - totalPrice;
      const savingsPercent = Math.round((savingsAmount / totalCompareAtPrice) * 100);
      
      console.log('Prices:', {
        unit: formatMoney(unitPrice),
        unitCompare: formatMoney(unitCompareAtPrice),
        total: formatMoney(totalPrice),
        totalCompare: formatMoney(totalCompareAtPrice),
        savings: formatMoney(savingsAmount),
        savingsPercent
      });
      
      // Update the price display
      let priceHTML = '';
      
      if (quantityNeeded > 1) {
        priceHTML = `<span class="price">${formatMoney(totalPrice)} for ${quantityNeeded} more</span>`;
      } else {
        priceHTML = `<span class="price">${formatMoney(totalPrice)}</span>`;
      }
      
      if (savingsAmount > 0) {
        priceHTML += `<span class="save-badge">Save ${savingsPercent}%</span>`;
      }
      
      // Check if element still exists before updating
      if (document.body.contains(priceElement)) {
        priceElement.innerHTML = priceHTML;
      } else {
        console.warn('Price element no longer in DOM, update skipped');
      }
    } catch (error) {
      console.error('Error updating bundle price:', error);
    }
  }

  // Update Sculptor prices in the crosssell section
  async updateSculptorCrosssellPrices() {
    try {
      console.log('Updating sculptor crosssell prices...');
      
      // Get current cart data to calculate sculptor count
      const sculptorData = await this.getCartSculptorCount();
      const sculptorCount = sculptorData.sculptorCount || 0;
      const sculptorTotalPrice = sculptorData.sculptorTotalPrice || '$0';
      const sculptorTotalAmount = parseFloat(sculptorTotalPrice.replace(/[^0-9.-]+/g, '')) * 100;
      console.log('Found sculptor count:', sculptorCount, 'Total amount:', (sculptorTotalAmount/100).toFixed(2));
      
      // Find all sculptor crosssell items
      const allItems = this.querySelectorAll('.crosssell-item');
      console.log('Found crosssell items:', allItems.length);
      
      // Get product data first to avoid multiple fetches
      const product = await this.getSculptorProduct();
      if (!product) {
        console.error('Could not fetch sculptor product data');
        return;
      }
      
      // Format money helper
      const formatMoney = (cents) => {
        const dollars = cents / 100;
        return '$' + dollars.toFixed(2).replace(/\.00$/, '');
      };
      
      // Bundle pricing constants
      // const BUNDLE_PRICE = 7999; // $79.99 in cents for 5 items
      const BUNDLE_PRICE = 6990; // $79.99 in cents for 5 items
      // const SINGLE_COMPARE_PRICE = product.compare_at_price || 3999; // $39.99 in cents
      const SINGLE_COMPARE_PRICE = product.compare_at_price || 3495; // $39.99 in cents
      // const SINGLE_PRICE = product.price || 3999;
      const SINGLE_PRICE = product.price || 3495;
      
      // Process each item
      for (const item of allItems) {
        // Check if this is a sculptor item
        const addButton = item.querySelector('[data-product-handle]');
        if (!addButton) {
          console.log('No add button found in item');
          continue;
        }
        
        const productHandle = addButton.dataset.productHandle;
        console.log('Found item with handle:', productHandle);
        
        if (!productHandle.includes('sculptor-2-0')) {
          console.log('Not a sculptor item, skipping');
          continue;
        }
        
        console.log('Processing sculptor item');
        
        // HIDE SCULPTOR ITEMS if we already have 5 or more in cart
        if (sculptorCount >= 4) {
          item.style.display = 'none';
          console.log('Hiding sculptor item because 4+ already in cart');
          continue;
        } else {
          // Make sure it's visible
          item.style.display = '';
        }
        
        // Get the price element
        const priceElement = item.querySelector('.crosssell-item__price .price');
        const comparePriceElement = item.querySelector('.crosssell-item__price .compare-price');
        
        if (!priceElement) {
          console.log('No price element found');
          continue;
        }
        
        // Calculate bundle pricing logic
        const TARGET_COUNT = 4;
        
        // Calculate how many more sculptors needed
        const quantityNeeded = Math.max(1, TARGET_COUNT - sculptorCount);
        console.log('Quantity needed to reach bundle:', quantityNeeded);
        
        // Calculate total price for all additional sculptors needed
        let totalPriceToShow = 0;
        
        // First, calculate price based on cart state
        if (sculptorCount === 3) {
          // Special completion price for the final item
          // totalPriceToShow = Math.min(1500, BUNDLE_PRICE - sculptorTotalAmount);
          totalPriceToShow = BUNDLE_PRICE - sculptorTotalAmount;
        } else if (sculptorCount > 0 && sculptorCount < 3) {
          // Prorated bundle completion price
          totalPriceToShow = BUNDLE_PRICE - sculptorTotalAmount;
        } else {
          // Full bundle price (no items yet)
          totalPriceToShow = BUNDLE_PRICE;
        }

        // Log price for debugging
        console.log('Price to show:', formatMoney(totalPriceToShow), 
                  'for', quantityNeeded, 'items');

        // Always set shipping text to default (outside the if statement)
        const shippingText = item.querySelector('.crosssell-item__shipping');
        if (shippingText) {
          shippingText.textContent = 'Free Shipping Included';
          shippingText.style.removeProperty('fontWeight');
          shippingText.style.removeProperty('color');
        }
        
        // Ensure we're not showing below $5 per item
        totalPriceToShow = Math.max(totalPriceToShow, 500 * quantityNeeded);
        
        console.log('Final price to display:', formatMoney(totalPriceToShow), 'Current element text:', priceElement.textContent);
        
        // Force update the price display
        priceElement.textContent = formatMoney(totalPriceToShow);
        
        // Replace it with this enhanced version:
        const titleElement = item.querySelector('.crosssell-item__title');
        if (titleElement && quantityNeeded > 1) {
          // Create a new structure with the quantity and free item
          titleElement.innerHTML = `
            ${quantityNeeded}x ${product.title}
            <span class="free-item">+ FREE SCULPTSHIRT™</span>
          `;
          
          // Add some styling for the free item text
          const freeItemElement = titleElement.querySelector('.free-item');
          if (freeItemElement) {
            freeItemElement.style.display = 'block';
            freeItemElement.style.color = '#4CAF50';
            freeItemElement.style.fontSize = '0.8em';
            freeItemElement.style.fontWeight = 'bold';
            freeItemElement.style.marginTop = '3px';
          }
        }
        
        // Update the button text to indicate multiple items
        // if (addButton && quantityNeeded > 1) {
        //   addButton.textContent = `+ ADD ${quantityNeeded}`;
        // }
        
        // Update compare price if it exists
        if (comparePriceElement) {
          comparePriceElement.textContent = formatMoney(SINGLE_COMPARE_PRICE * quantityNeeded);
        } else if (SINGLE_COMPARE_PRICE * quantityNeeded > totalPriceToShow) {
          // Create compare price element if it doesn't exist
          const newComparePrice = document.createElement('s');
          newComparePrice.className = 'compare-price';
          newComparePrice.textContent = formatMoney(SINGLE_COMPARE_PRICE * quantityNeeded);
          priceElement.insertAdjacentElement('beforebegin', newComparePrice);
        }
      }
    } catch (error) {
      console.error('Error updating sculptor crosssell prices:', error);
    }
  }

  // Generate modal HTML without server template
  generateModalHTML(product, quantityNeeded) {
    console.log('Generating modal HTML manually for', product.title, 'Quantity needed:', quantityNeeded);
    
    // Get product data
    const { images, options, variants, price } = product;
    const productTitle = product.title || 'Product';
    
    // Compile the HTML
    const html = `
      <button type="button" class="quick-add-modal__close" aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15 5L5 15M5 5L15 15" stroke="black" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="quick-add-product">
        <!-- Modal content here -->
      </div>
    `;
    
    // Populate modal with HTML
    this.populateModal(product, quantityNeeded);
    
    // Setup selectors and update price
    this.setupBundleSelectors(product, productTitle, quantityNeeded);
    
    // Update price
    this.updateBundlePrice(product, quantityNeeded);
  }

  // Add missing methods for the scroll-snap based slider

  getCurrentPage() {
    if (!this.container) return 0;
    
    const scrollLeft = this.container.scrollLeft;
    const itemWidth = this.getItemWidth();
    const containerWidth = this.container.clientWidth;
    
    // For small screens (< 575px), use percentage-based calculation
    if (window.innerWidth <= 575) {
      // On small mobile, we're using 90% width items
      return Math.round(scrollLeft / (containerWidth * 0.9));
    }
    
    // On desktop and larger screens, calculate based on 50% width items
    const itemsPerPage = 2; // We show 2 items at once
    const pageWidth = itemWidth * itemsPerPage;
    
    // Get the closest multiple of pageWidth
    return Math.round(scrollLeft / pageWidth);
  }

  scrollToPage(pageIndex) {
    if (!this.container) return;
    
    // Ensure pageIndex is within bounds
    pageIndex = Math.max(0, Math.min(pageIndex, this.totalPages - 1));
    
    const containerWidth = this.container.clientWidth;
    const itemWidth = this.getItemWidth();
    
    // Calculate scroll position based on screen size
    let scrollLeft;
    
    // For small screens (< 575px), use percentage-based calculation
    if (window.innerWidth <= 575) {
      // On small mobile, each "page" is 90% of container width
      scrollLeft = pageIndex * (containerWidth * 0.9);
    } else {
      // On desktop and larger screens, we have 2 items per page
      const itemsPerPage = 2;
      scrollLeft = pageIndex * (itemWidth * itemsPerPage);
    }
    
    // Scroll with smooth behavior
    this.container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
    
    // Update UI immediately to prevent flicker
    this.currentIndex = pageIndex;
    this.updateActiveDot(pageIndex);
    this.updateSliderButtons();
    
    // Clear any existing debounced updates
    if (this._updateStateTimeout) {
      clearTimeout(this._updateStateTimeout);
    }
    
    // Add a delayed update to ensure UI reflects the final position
    this._updateStateTimeout = setTimeout(() => {
      this.updateSliderState();
    }, 300); // Adjust timing to match scroll animation duration
  }

  snapToNearestSlide() {
    if (!this.container) return;
    
    const scrollLeft = this.container.scrollLeft;
    const itemWidth = this.getItemWidth();
    const containerWidth = this.container.clientWidth;
    
    let targetPage;
    
    // For small screens (< 575px), use percentage-based calculation
    if (window.innerWidth <= 575) {
      // On mobile, we're using 90% width items
      const slideWidth = containerWidth * 0.9;
      targetPage = Math.round(scrollLeft / slideWidth);
    } else {
      // On desktop and larger screens, we show 2 items per page
      const pageWidth = itemWidth * 2;
      targetPage = Math.round(scrollLeft / pageWidth);
    }
    
    // Scroll to the calculated page
    this.scrollToPage(targetPage);
  }

  updateSliderState() {
    // Get the current page more reliably
    const currentPage = this.getCurrentPage();
    
    // Only update if the page has actually changed
    if (currentPage !== this.currentIndex) {
      this.currentIndex = currentPage;
      this.updateActiveDot(currentPage);
      this.updateSliderButtons();
    }
  }

  // Additional functions needed for cart cross-sell slider

  // Add a monitoring system to detect and repair any damage to our component
  setupResilienceMonitor() {
    if (!window.MutationObserver) return;
    
    // Monitor the slider container for changes
    const monitorSlider = () => {
      if (!this.sliderContainer) return;
      
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // If a child was removed or added
          if (mutation.type === 'childList') {
            if (mutation.removedNodes.length > 0 || mutation.addedNodes.length > 0) {
              // Check if our slider still works
              if (this.isolatedSlider) {
                try {
                  // Test if the slider API still works
                  const position = this.isolatedSlider.getCurrentPosition();
                  // If we got here, the API is still working
                } catch (e) {
                  console.warn('Slider API damaged, reinitializing:', e);
                  this.reinitializeSlider();
                }
              } else {
                // No isolatedSlider reference - something broke it
                console.warn('Slider reference lost, reinitializing');
                this.reinitializeSlider();
              }
            }
          }
        }
      });
      
      observer.observe(this.sliderContainer.parentNode, { 
        childList: true,
        subtree: true 
      });
      
      this._sliderObserver = observer;
    };
    
    monitorSlider();
  }

  // Method to reinitialize the slider if needed
  reinitializeSlider() {
    // Clean up old slider
    if (this._destroySlider) {
      try {
        this._destroySlider();
      } catch (e) {
        console.warn('Error cleaning up slider:', e);
      }
    }
    
    // Try to re-find the elements in case they were replaced
    this.slider = this.querySelector('.cart-crosssell-new__slider');
    this.sliderContainer = this.querySelector('.slider-container');
    
    if (!this.slider || !this.sliderContainer) {
      console.warn('Cannot reinitialize slider - elements not found');
      return;
    }
    
    // Reinitialize slider
    this.initSimplifiedSlider();
    
    // Rebind quick add buttons in case they were replaced
    this.bindQuickAddButtons();
    
    console.log('Slider reinitialized successfully');
  }

  async addBundleToCart(product, selectedSize, selectedColors) {
    console.log('Adding bundle to cart with:', { size: selectedSize, colors: selectedColors });
    
    // Validate selections
    if (!selectedSize) {
      console.error('Missing size selection');
      throw new Error('Please select a size');
    }
    
    const validColors = selectedColors.filter(color => !!color);
    if (validColors.length !== selectedColors.length) {
      console.error('Missing color selection(s):', { 
        selected: validColors.length, 
        needed: selectedColors.length,
        colors: selectedColors 
      });
      throw new Error('Please select colors for all items');
    }
    
    try {
      // Check cart state before proceeding
      const initialCartState = await fetch('/cart.js').then(res => res.json());
      console.log('Initial cart state before adding bundle:', {
        itemCount: initialCartState.item_count,
        totalPrice: initialCartState.total_price
      });
      
      // Find variant IDs for each color/size combination
      const variantIds = [];
      
      for (const color of selectedColors) {
        const variantId = this.findVariantId(product, selectedSize, color);
        if (!variantId) {
          console.error(`Could not find variant for size: ${selectedSize}, color: ${color}`);
          throw new Error(`Could not find the variant for ${color} in size ${selectedSize}`);
        }
        variantIds.push(variantId);
      }
      
      console.log('Found variant IDs:', variantIds);
      
      // Add items to cart sequentially to maintain order
      for (let i = 0; i < variantIds.length; i++) {
        console.log(`Adding item ${i+1}/${variantIds.length} to cart`);
        
        // Check if cart state has changed during the process
        if (i > 0) {
          const currentCartState = await fetch('/cart.js').then(res => res.json());
          if (currentCartState.item_count !== initialCartState.item_count + i) {
            console.warn('Cart state changed during add process, aborting');
            throw new Error('Cart state changed during add process. Please try again.');
          }
        }
        
        const formData = {
          items: [{
            id: variantIds[i],
            quantity: 1,
            properties: {  // Added this properties object
              '_bundle_item': 'true',
              '_bundle_source': 'cart-crosssell',
              '_bundle_position': `${i+1}/${variantIds.length}`
            }
          }]
        };
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error(`Error adding item ${i+1}:`, errorData);
          throw new Error(errorData.description || `Error adding item ${i+1} to cart`);
        }
        
        const addedItem = await response.json();
        console.log(`Item ${i+1} added successfully:`, addedItem.title);
      }
      
      // Update cart count bubble
      this.updateCartCountBubble();
      
      // Refresh cart
      await this.refreshCart(true);
      
      console.log('Bundle added to cart successfully');
      return true;
    } catch (error) {
      console.error('Error in addBundleToCart:', error);
      throw error;
    }
  }

  // Get sculptor product data
  async getSculptorProduct() {
    try {
      // Try multiple possible handles for the sculptor product
      const SCULPTOR_HANDLES = ['sculptor-2-0', 'sculptor', 'sculptor-2'];
      
      // Try each handle until we find one that works
      for (const handle of SCULPTOR_HANDLES) {
        try {
          console.log('DEBUG - Trying to fetch sculptor product data for handle:', handle);
          
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            console.log(`Handle ${handle} not found, trying next...`);
            continue;
          }
          
          const product = await response.json();
          console.log('DEBUG - Sculptor product data:', {
            id: product.id,
            title: product.title,
            price: product.price,
            comparePrice: product.compare_at_price,
            variants: product.variants.length
          });
          
          return product;
        } catch (error) {
          console.log(`Error fetching sculptor product with handle ${handle}:`, error);
          // Continue to next handle
        }
      }
      
      // If we get here, none of the handles worked
      throw new Error('Could not find sculptor product with any known handle');
    } catch (error) {
      console.error('Error fetching sculptor product:', error);
      return null;
    }
  }

  // Update bundle price display
  async updateBundlePrice(product, quantityNeeded) {
    const priceContainer = this.modal.querySelector('.quick-add-product__price');
    const priceEachContainer = this.modal.querySelector('.bundle-price-each');
    
    if (priceContainer) {
      try {
        // Use the bundle pricing constants
        // const BUNDLE_PRICE = 7999; // $79.99 in cents for 5 items
        // const singlePrice = product.price || 3999;
        const BUNDLE_PRICE = 6990; // $79.99 in cents for 5 items
        const singlePrice = product.price || 3495;
        
        // Calculate price using the same bundle logic
        let totalPrice;
        if (quantityNeeded > 0) {
          // Get fresh cart data
          const sculptorData = await this.getCartSculptorCount();
          const sculptorCount = sculptorData.sculptorCount || 0;
          const sculptorTotalPrice = sculptorData.sculptorTotalPrice || '$0';
          const sculptorTotalAmount = parseFloat(sculptorTotalPrice.replace(/[^0-9.-]+/g, '')) * 100;
          
          console.log('Updating bundle price - Sculptor count:', sculptorCount, 
                     'Amount:', (sculptorTotalAmount/100).toFixed(2),
                     'Quantity needed:', quantityNeeded);
          
          // Calculate bundle completion price
          if (sculptorCount > 0) {
            // Calculate remaining amount to complete bundle
            totalPrice = BUNDLE_PRICE - sculptorTotalAmount;
            // Ensure minimum pricing
            totalPrice = Math.max(totalPrice, 500 * quantityNeeded); // Min $5 per item
          } else {
            // No items in cart, use the bundle price
            totalPrice = BUNDLE_PRICE;
          }
        } else {
          // Just one item, use the regular price
          totalPrice = singlePrice;
        }
        
        // Format the currency
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        
        // Update the price display with the new value
        const priceDisplay = formatter.format(totalPrice / 100);
        const priceElement = priceContainer.querySelector('.price');
        if (priceElement) {
          priceElement.textContent = quantityNeeded > 1 ? 
            `${priceDisplay} for ${quantityNeeded} more` : 
            priceDisplay;
        }
        
        // Update price per item if needed
        if (priceEachContainer && quantityNeeded > 1) {
          const pricePerItem = (totalPrice / quantityNeeded) / 100;
          priceEachContainer.textContent = `($${pricePerItem.toFixed(2)} each)`;
          priceEachContainer.style.display = 'inline';
        } else if (priceEachContainer) {
          priceEachContainer.style.display = 'none';
        }
      } catch (error) {
        console.error('Error updating bundle price:', error);
      }
    }
  }
}

// PubSub implementation - same as in cart.js for consistency
function subscribe(eventName, callback) {
  const customEvent = new CustomEvent(eventName, { detail: { callback } });
  document.dispatchEvent(customEvent);

  return function unsubscribe() {
    const customEvent = new CustomEvent(eventName + ':unsubscribe', {
      detail: { callback },
    });
    document.dispatchEvent(customEvent);
  };
}

customElements.define('cart-crosssell-new', CartCrosssellNew);

class QuickAddModal extends HTMLElement {
  constructor() {
    super();
    
    // Create overlay if it doesn't exist
    if (!this.querySelector('.quick-add-modal__overlay')) {
      const overlay = document.createElement('div');
      overlay.classList.add('quick-add-modal__overlay');
      this.prepend(overlay);
    }
    
    // Set up global ESC key listener
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.setAttribute('aria-hidden', 'true');
      }
    });
    
    // Handle overlay clicks
    this.addEventListener('click', (event) => {
      if (event.target.classList.contains('quick-add-modal__overlay')) {
        this.setAttribute('aria-hidden', 'true');
      }
    });
  }
}

customElements.define('quick-add-modal', QuickAddModal);
